import { PROMPT_VIA_LABELS, type PromptVia } from '@reflex/shared';
import { cn } from '@reflex/ui/lib/utils';
import { ChiefVisorIcon } from '@reflex/ui/components/brand/ChiefVisorIcon';
import { UserAvatar } from '@reflex/ui/components/shared/UserAvatar';

/**
 * The avatar for a user prompt, which is not always a plain user avatar: a
 * prompt Chief relayed is Chief's message *for* someone, so the relay takes
 * the main circle and the human it was sent for is badged onto its corner.
 * Without a `via` this is exactly `UserAvatar`, so callers can render every
 * prompt through this one component.
 *
 * One component owns the mapping so the chat pane and the raw event stream
 * cannot disagree about what a relayed prompt looks like.
 */
export function PromptSenderAvatar({
  via,
  userId,
  userName,
  avatarUrl,
  className,
}: {
  /** The agent that sent the prompt for the user, if any. */
  via?: PromptVia | null;
  userId?: string;
  userName?: string;
  avatarUrl?: string;
  className?: string;
}) {
  if (!via) {
    return (
      <UserAvatar
        size="sm"
        userId={userId}
        userName={userName}
        avatarUrl={avatarUrl}
        className={className}
      />
    );
  }
  // The two circles are one picture with one meaning, so the composition —
  // not either mark — carries the accessible name; screen readers otherwise
  // read "Chief" and the user's avatar as two unrelated images.
  const label = userName ? `${PROMPT_VIA_LABELS[via]}, for ${userName}` : PROMPT_VIA_LABELS[via];
  return (
    <span
      // Both circles stay inside the box: transcript rows set
      // `content-visibility: auto`, whose paint containment clips to the row's
      // padding edge, so a badge hung outside the bounds can lose a side.
      className={cn('relative block size-8 shrink-0', className)}
      data-testid={`prompt-sender-avatar-via-${via}`}
      role="img"
      aria-label={label}
      title={label}
    >
      <span
        aria-hidden="true"
        className="absolute top-0 left-0 flex size-7 items-center justify-center rounded-full border border-border bg-muted/40"
      >
        <ChiefVisorIcon className="size-4.5 text-foreground/90" />
      </span>
      {/* The cutout is what separates the two circles: without it the badge
          reads as a hole punched in Chief's jaw on a dark surface. It is a
          border rather than a ring for the same reason `UserAvatar` gives —
          a ring paints outside the border box, so it would be clipped here. */}
      <span aria-hidden="true">
        <UserAvatar
          size="xs"
          userId={userId}
          userName={userName}
          avatarUrl={avatarUrl}
          className="absolute right-0 bottom-0 border-2 border-background"
        />
      </span>
    </span>
  );
}
